// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/system";
import { Close } from "@mui/icons-material";
import { useAppDispatch } from "Hooks";
import { DeleteProjectThunk } from "Features/project/projectThunk";
import { useState } from "react";
import stylesDef from "./styles";
import { getAllFoldersThunk } from "Features/folderList/folderListThunk";
import { SetFoldersAction } from "Features/projectList/projectListActions";

interface DeleteProjectDialogProps {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  projectId: string;
}

const DeleteProjectDialog = ({ showDialog, setShowDialog, projectId }: DeleteProjectDialogProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const styles = stylesDef(theme);
  const [loading, setLoading] = useState(false);
  
  const onCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setShowDialog(false);
  };

  const onDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setLoading(true);
    await dispatch(DeleteProjectThunk(projectId));
    // Fetch all folders after deletion to update the folder state
    const response = await dispatch(getAllFoldersThunk());
    if (response) {
      dispatch(SetFoldersAction(response));
    }
    setLoading(false);
    setShowDialog(false);
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => setShowDialog(false)}
      keepMounted={false}
      sx={styles.dialog}
    >
      <Box sx={styles.titleSection}>
        <DialogTitle sx={styles.dialogTitle}>
          Delete Project
        </DialogTitle>
        <IconButton
          color="inherit"
          onClick={() => setShowDialog(false)}
          aria-label="Close"
          sx={styles.closeButton}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1" sx={styles.dialogText}>
          You're deleting this project. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={onCancel} sx={styles.cancelButton}>
          Close
        </Button>
        <Button
          onClick={onDelete}
          disabled={loading}
          variant="contained"
        >
          {loading ? (
            <CircularProgress size={24} sx={styles.buttonProgress} />
          ) : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteProjectDialog;
